import type {
    ApplicationType,
    FormDataType,
    FormElementType,
    OptionType,
    RestrictedVisibility,
    VisibilityCriterion,
    VisibilityOperator,
} from './types';

export type RequiredOverride = true | false | null;

/**
 * Reservert felt-navn for betingelser som baserer seg på HVILKEN søknadsmal
 * søknaden er levert på (kun relevant for rapportmaler). Verdien som
 * sammenlignes er søknadens `type` (gavekategori) — se VisibilityContext.
 */
export const APPLICATION_TEMPLATE_FIELD = '__application_template';

/**
 * Ekstra kontekst for kriterie-evaluering som ikke ligger i formData.
 * `applicationType` = `application.type` for søknaden rapporten gjelder.
 */
export type VisibilityContext = {
    applicationType?: string | number;
};

/** Norske operatør-labels brukt i debug-overlayet ("Vises fordi …"). */
const OPERATOR_LABELS: Record<VisibilityOperator, string> = {
    EQ: 'er lik',
    IN: 'finnes i',
    NIN: 'finnes ikke i',
    GT: 'større enn',
    GTE: 'større enn eller lik',
    LT: 'mindre enn',
    LTE: 'mindre enn eller lik',
    LIKE: 'inkluderer',
    NOT_LIKE: 'inkluderer ikke',
};

/** Itererer alle elementer i hele applikasjonen (inkl. radiogroupdepending). */
function collectAllElements(application: ApplicationType): FormElementType[] {
    const out: FormElementType[] = [];
    for (const section of application.sections ?? []) {
        const forms = (section as any).forms;
        if (!Array.isArray(forms)) continue;
        for (const form of forms) {
            for (const el of form.elements ?? []) {
                out.push(el);
                if ((el as any).type === 'radiogroupdepending' && Array.isArray((el as any).depends)) {
                    for (const dep of (el as any).depends) {
                        for (const inner of dep.innerElements ?? []) {
                            out.push(inner);
                        }
                    }
                }
            }
        }
    }
    return out;
}

function resolveOptionLabel(field: FormElementType | undefined, value: string): string {
    if (!field) return value;
    const opts: OptionType[] = (field as any).options ?? (field as any).alternatives ?? [];
    const found = opts.find((o) => String(o.value) === String(value));
    return found?.label ?? value;
}

function formatCriterionValue(field: FormElementType | undefined, value: VisibilityCriterion['value']): string {
    if (Array.isArray(value)) {
        if (value.length === 0) return '(ingen verdier)';
        return value.map((v) => resolveOptionLabel(field, String(v))).join(', ');
    }
    if (typeof value === 'number') return String(value);
    if (value === '' || value === undefined || value === null) return '(ingen verdi)';
    return resolveOptionLabel(field, String(value));
}

/**
 * Formaterer ett kriterium til lesbar tekst, f.eks.:
 *   "Kategori er lik Kunst og kultur"
 * Brukes av Debug-overlayet i søknadsskjemaet for å forklare hvorfor et
 * begrenset felt vises.
 */
export function formatRestrictedCriterion(
    criterion: VisibilityCriterion,
    application: ApplicationType
): string {
    const all = collectAllElements(application);
    const field = all.find((el) => el.name === criterion.field);
    const fieldLabel = field?.label || criterion.field || '(intet felt)';
    const opLabel = OPERATOR_LABELS[criterion.operator] ?? criterion.operator;
    const valueLabel = formatCriterionValue(field, criterion.value);
    return `${fieldLabel} ${opLabel} ${valueLabel}`;
}

/** Slår sammen alle kriteriene i en restrictedVisibility til én tekst. */
export function formatRestrictedVisibility(
    restricted: RestrictedVisibility | undefined,
    application: ApplicationType
): string {
    if (!restricted || !restricted.criteria || restricted.criteria.length === 0) return '';
    return restricted.criteria
        .filter((c) => c.field)
        .map((c) => formatRestrictedCriterion(c, application))
        .join(' og ');
}

/**
 * Evaluerer "Begrenset visning"-kriterier fra et form-element mot søkerens
 * `formData`. Returnerer `true` hvis elementet skal vises.
 *
 * Reglene speiler operatørene i `lyfti-form-builder/src/helpers/restrictedVisibility.ts`:
 *   - EQ        verdi == kriterium
 *   - IN/NIN    verdi finnes / finnes ikke i array
 *   - GT/GTE/LT/LTE  numerisk sammenligning
 *   - LIKE/NOT_LIKE  case-insensitive substring
 *
 * Hvis et kriterium har tomt `field`-navn ignoreres det (tilsvarer at admin
 * ikke har fullført opprettelsen). Hvis criteria-arrayet er tomt vises
 * elementet. Alle kriteriene må passere (AND-semantikk).
 */
export function isElementVisible(
    restricted: RestrictedVisibility | undefined,
    formData: FormDataType,
    context?: VisibilityContext
): boolean {
    if (!restricted) return true;
    const criteria = (restricted.criteria ?? []).filter((c) => c.field);
    if (!criteria.length) return true;

    // show criteria (default): ALL must pass — element hidden if any fails
    const showCriteria = criteria.filter((c) => !c.action || c.action === 'show');
    if (showCriteria.length > 0) {
        for (const c of showCriteria) {
            if (!evaluateCriterion(c, formData, context)) {
                return false;
            }
        }
    }

    // hide criteria: element hidden if ANY matches
    const hideCriteria = criteria.filter((c) => c.action === 'hide');
    if (hideCriteria.some((c) => evaluateCriterion(c, formData, context))) {
        return false;
    }

    return true;
}

/**
 * Returns whether condition-based rules override the element's `required` flag.
 *
 * The `require` action is direction-aware:
 *   - element.required=false + criterion matches → return true  (add required)
 *   - element.required=true  + criterion matches → return false (remove required)
 *
 * The legacy `optional` action always forces optional (backward compat).
 *
 * Returns `null` when no condition-based override applies.
 */
export function getElementRequiredOverride(
    restricted: RestrictedVisibility | undefined,
    formData: FormDataType,
    elementRequired: boolean = false,
    context?: VisibilityContext
): RequiredOverride {
    if (!restricted) return null;
    const criteria = (restricted.criteria ?? []).filter((c) => c.field);

    // optional action (legacy / backward compat) — always forces optional
    const optionalCriteria = criteria.filter((c) => c.action === 'optional');
    if (optionalCriteria.some((c) => evaluateCriterion(c, formData, context))) return false;

    // require action — inverts based on element's base required state
    const requireCriteria = criteria.filter((c) => c.action === 'require');
    if (requireCriteria.some((c) => evaluateCriterion(c, formData, context))) {
        return !elementRequired as boolean;
    }

    return null;
}

function evaluateCriterion(c: VisibilityCriterion, formData: FormDataType, context?: VisibilityContext): boolean {
    // Spesialfelt: betingelse på hvilken søknadsmal søknaden er levert på.
    // Verdien hentes fra context (søknadens type), ikke fra formData.
    const actual = c.field === APPLICATION_TEMPLATE_FIELD
        ? context?.applicationType
        : formData?.[c.field];

    switch (c.operator) {
        case 'EQ':
            return equalsLoose(actual, c.value);
        case 'IN':
            return inArray(actual, toArray(c.value));
        case 'NIN':
            return !inArray(actual, toArray(c.value));
        case 'GT':
            return toNumber(actual) > toNumber(c.value);
        case 'GTE':
            return toNumber(actual) >= toNumber(c.value);
        case 'LT':
            return toNumber(actual) < toNumber(c.value);
        case 'LTE':
            return toNumber(actual) <= toNumber(c.value);
        case 'LIKE':
            return likeMatch(actual, c.value);
        case 'NOT_LIKE':
            return !likeMatch(actual, c.value);
        case 'EMPTY':
            return isValueEmpty(actual);
        default:
            return true;
    }
}

function isValueEmpty(v: unknown): boolean {
    if (v === undefined || v === null || v === '') return true;
    if (Array.isArray(v)) return v.length === 0;
    return false;
}

function equalsLoose(actual: unknown, expected: unknown): boolean {
    if (Array.isArray(actual)) {
        return actual.some((v) => String(v) === String(expected));
    }
    if (actual === null || actual === undefined) return false;
    return String(actual) === String(expected);
}

function inArray(actual: unknown, expectedArr: string[]): boolean {
    if (!expectedArr.length) return false;
    if (Array.isArray(actual)) {
        return actual.some((v) => expectedArr.includes(String(v)));
    }
    if (actual === null || actual === undefined) return false;
    return expectedArr.includes(String(actual));
}

function likeMatch(actual: unknown, expected: unknown): boolean {
    if (actual === null || actual === undefined) return false;
    const needle = String(expected ?? '').toLowerCase();
    if (!needle) return false;
    if (Array.isArray(actual)) {
        return actual.some((v) => String(v).toLowerCase().includes(needle));
    }
    return String(actual).toLowerCase().includes(needle);
}

function toArray(v: string | number | string[]): string[] {
    if (Array.isArray(v)) return v.map((x) => String(x));
    if (v === '' || v === null || v === undefined) return [];
    return [String(v)];
}

function toNumber(v: unknown): number {
    if (typeof v === 'number') return v;
    if (typeof v === 'string' && v.trim() !== '') {
        const n = Number(v.replace(/\s/g, '').replace(',', '.'));
        return Number.isFinite(n) ? n : NaN;
    }
    return NaN;
}
